import React, { useMemo, useState, Component } from 'react';
import { ComponentType } from 'react';
import { useData } from '../../store/DataContext';
import { MenuIcon, XIcon, ChevronLeftIcon, ChevronRightIcon, LayoutDashboardIcon, PillIcon, PackageCheckIcon, ClipboardListIcon, TruckIcon, SlidersHorizontalIcon, ArrowLeftRightIcon, CalendarX2Icon, AlertTriangleIcon, BarChart3Icon, HistoryIcon, FileTextIcon } from 'lucide-react';
import { PharmacyOverview } from './PharmacyOverview';
import { PharmacistDashboard } from './PharmacistDashboard';
import { InventoryList } from './InventoryList';
import { ReceiveStock } from './ReceiveStock';
import { PurchaseOrders } from './PurchaseOrders';
import { Suppliers } from './Suppliers';
import { StockActions } from './StockActions';
import { ExpiredMedicines } from './ExpiredMedicines';
import { LowStockAlerts } from './LowStockAlerts';
import { InventoryReports } from './InventoryReports';
import { StockMovementHistory } from './StockMovementHistory';
import { PharmacyDispensing } from './PharmacyDispensing';
import { expiryAlertWindow, isLowStockOnly, isOutOfStock } from './pharmacyUtils';
export type PharmacySection = 'overview' | 'dispensing' | 'inventory' | 'receive-stock' | 'purchase-orders' | 'suppliers' | 'adjustments' | 'returns' | 'expired' | 'low-stock' | 'reports' | 'movement-history';
type NavItem = {
  id: PharmacySection;
  label: string;
  icon: ComponentType<{
    className?: string;
  }>;
  count?: number;
  tone?: 'red' | 'amber' | 'blue';
};
type NavGroup = {
  label: string;
  items: NavItem[];
};
export function PharmacyModule() {
  const {
    currentUser,
    medicines,
    medicineBatches,
    purchaseOrders
  } = useData();
  const dispenseOnly = currentUser?.role === 'Pharmacy';
  const [section, setSection] = useState<PharmacySection>(dispenseOnly ? 'dispensing' : 'overview');
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const alerts = useMemo(() => ({
    low: medicines.filter(isLowStockOnly).length,
    out: medicines.filter(isOutOfStock).length,
    expiring: medicineBatches.filter(batch => expiryAlertWindow(batch) !== null).length,
    purchaseOrders: purchaseOrders.filter(order => order.status === 'Pending').length
  }), [medicineBatches, medicines, purchaseOrders]);
  if (!currentUser || !['Administrator', 'Pharmacy', 'Pharmacy Manager'].includes(currentUser.role)) return <div className="rounded-xl border border-red-200 bg-red-50 p-8 text-center text-red-700" data-id="element-5068" data-mp="2otzxorb23qp">
        You do not have permission to access the Pharmacy workspace.
      </div>;
  const readOnly = currentUser.role === 'Administrator';
  const manager = currentUser.role === 'Pharmacy Manager';
  const managerGroups: NavGroup[] = [{
    label: 'Pharmacy',
    items: [{
      id: 'overview',
      label: 'Pharmacist Dashboard',
      icon: LayoutDashboardIcon
    }, {
      id: 'dispensing',
      label: 'Dispensing',
      icon: ClipboardListIcon
    }, {
      id: 'inventory',
      label: 'Inventory',
      icon: PillIcon
    }, {
      id: 'receive-stock',
      label: 'Receive New Stock',
      icon: PackageCheckIcon
    }, {
      id: 'adjustments',
      label: 'Stock Adjustments',
      icon: SlidersHorizontalIcon
    }, {
      id: 'returns',
      label: 'Medicine Returns',
      icon: ArrowLeftRightIcon
    }, {
      id: 'expired',
      label: 'Expired Medicines',
      icon: CalendarX2Icon,
      count: alerts.expiring,
      tone: medicineBatches.some(batch => expiryAlertWindow(batch) === 'Expired') ? 'red' : 'amber'
    }, {
      id: 'low-stock',
      label: 'Low Stock Alerts',
      icon: AlertTriangleIcon,
      count: alerts.low + alerts.out + alerts.expiring,
      tone: alerts.out ? 'red' : 'amber'
    }]
  }, {
    label: 'Procurement',
    items: [{
      id: 'purchase-orders',
      label: 'Purchase Orders',
      icon: FileTextIcon,
      count: alerts.purchaseOrders,
      tone: 'blue'
    }, {
      id: 'suppliers',
      label: 'Suppliers',
      icon: TruckIcon
    }]
  }, {
    label: 'Insights',
    items: [{
      id: 'reports',
      label: 'Inventory Reports',
      icon: BarChart3Icon
    }, {
      id: 'movement-history',
      label: 'Stock Movement History',
      icon: HistoryIcon
    }]
  }];
  const groups: NavGroup[] = readOnly ? [{
    label: 'Monitoring',
    items: [{
      id: 'overview',
      label: 'Pharmacy Overview',
      icon: LayoutDashboardIcon
    }, {
      id: 'inventory',
      label: 'Pharmacy Inventory',
      icon: PillIcon
    }, {
      id: 'low-stock',
      label: 'Low Stock Alerts',
      icon: AlertTriangleIcon,
      count: alerts.low + alerts.out + alerts.expiring,
      tone: alerts.out ? 'red' : 'amber'
    }, {
      id: 'expired',
      label: 'Expired Medicines',
      icon: CalendarX2Icon,
      count: alerts.expiring,
      tone: medicineBatches.some(batch => expiryAlertWindow(batch) === 'Expired') ? 'red' : 'amber'
    }]
  }, {
    label: 'Audit & reports',
    items: [{
      id: 'reports',
      label: 'Inventory Reports',
      icon: BarChart3Icon
    }, {
      id: 'movement-history',
      label: 'Stock Movement History',
      icon: HistoryIcon
    }]
  }] : manager ? managerGroups : [{
    label: 'Dispensing desk',
    items: [{
      id: 'dispensing',
      label: 'Dispensing',
      icon: ClipboardListIcon
    }]
  }];
  const select = (next: PharmacySection) => {
    setSection(next);
    setMobileOpen(false);
  };
  const content = () => {
    if (dispenseOnly) return <PharmacyDispensing data-id="element-5069" data-mp="wwndmjk2nqd4" />;
    switch (section) {
      case 'overview':
        return manager ? <PharmacistDashboard onNavigate={select} data-id="element-5070" data-mp="10xz22lr7m5jq" /> : <PharmacyOverview onNavigate={select} readOnly={readOnly} data-id="element-5071" data-mp="n12fwxm1dc96" />;
      case 'dispensing':
        return <PharmacyDispensing onBack={() => select('overview')} data-id="element-5072" data-mp="ryvjdivyfs5r" />;
      case 'inventory':
        return <InventoryList readOnly={readOnly} data-id="element-5073" data-mp="8ede2p4bamzm" />;
      case 'receive-stock':
        return <ReceiveStock data-id="element-5074" data-mp="3g27qod86um5" />;
      case 'purchase-orders':
        return <PurchaseOrders data-id="element-5075" data-mp="ibrmc1g305gu" />;
      case 'suppliers':
        return <Suppliers data-id="element-5076" data-mp="idcghpyhhme6" />;
      case 'adjustments':
        return <StockActions mode="adjustment" data-id="element-5077" data-mp="dceypehdyud7" />;
      case 'returns':
        return <StockActions mode="return" data-id="element-5078" data-mp="dfax13x5bl26" />;
      case 'expired':
        return <ExpiredMedicines data-id="element-5079" data-mp="rb4y50roh64q" />;
      case 'low-stock':
        return <LowStockAlerts data-id="element-5080" data-mp="me66covhlb9p" />;
      case 'reports':
        return <InventoryReports data-id="element-5081" data-mp="mbjy0n0rurqf" />;
      case 'movement-history':
        return <StockMovementHistory data-id="element-5082" data-mp="111hhwqro4v9t" />;
    }
  };
  const navigation = (mobile = false) => <nav className="space-y-5" aria-label="Pharmacy navigation" data-id="element-5083" data-mp="w364y762u4nd">
      {groups.map(group => <div key={group.label} data-id="element-5084" data-mp="1a2fl56ua8yrc">
          <p className={`mb-2 px-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400 ${collapsed && !mobile ? 'sr-only' : ''}`} data-id="element-5085" data-mp="1a3qmz1463wft">
            {group.label}
          </p>
          <div className="space-y-0.5" data-id="element-5086" data-mp="2cgbm3fu6fukh">
            {group.items.map(item => {
          const Icon = item.icon;
          const active = section === item.id;
          return <button key={item.id} type="button" onClick={() => select(item.id)} title={collapsed && !mobile ? item.label : undefined} className={`group flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-1 ${active ? 'bg-teal-50 text-teal-800' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-950'} ${collapsed && !mobile ? 'justify-center px-2' : ''}`} data-id="element-5087" data-mp="2mf99gffv9i1w">
                  <Icon className={`h-4 w-4 shrink-0 ${active ? 'text-teal-700' : 'text-slate-400 group-hover:text-slate-600'} ${collapsed && !mobile ? '' : 'mr-3'}`} data-id="element-5088" data-mp="1b043n2kfv3e2" />
                  {(!collapsed || mobile) && <>
                      <span className="truncate" data-id="element-5089" data-mp="12ik5v375m5nc">{item.label}</span>
                      {item.count ? <span className={`ml-auto min-w-5 rounded-full px-1.5 py-0.5 text-center text-[11px] font-bold ${item.tone === 'red' ? 'bg-red-100 text-red-700' : item.tone === 'amber' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`} data-id="element-5090" data-mp="ygqbtdrgrzw5">
                          {item.count}
                        </span> : null}
                    </>}
                </button>;
        })}
          </div>
        </div>)}
    </nav>;
  return <div className="-m-4 flex min-h-[calc(100vh-4rem)] bg-slate-50 sm:-m-6 lg:-m-8" data-id="element-5091" data-mp="at8mdovo9dwc">
      <aside className={`hidden shrink-0 border-r border-slate-200 bg-white lg:flex lg:flex-col ${collapsed ? 'w-[76px]' : 'w-[264px]'} transition-[width] duration-200`} data-id="element-5092" data-mp="5rrsidblurp9">
        <div className={`border-b border-slate-100 ${collapsed ? 'p-3' : 'p-5'}`} data-id="element-5093" data-mp="tmhrl7vhaj5q">
          <div className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'}`} data-id="element-5094" data-mp="plfogfyfxmyh">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-teal-50 text-teal-700" data-id="element-5095" data-mp="13cuakvnna49r">
              <PillIcon className="h-5 w-5" data-id="element-5096" data-mp="ygm0hwob119g" />
            </div>
            {!collapsed && <div data-id="element-5097" data-mp="ap885qq5l5oh">
                <p className="text-sm font-bold text-slate-900" data-id="element-5098" data-mp="aqrmvcw5v8o5">BMED Care</p>
                <p className="text-xs font-medium text-pink-600" data-id="element-5099" data-mp="6p63r9t19y5i">
                  {manager ? 'Pharmacist' : dispenseOnly ? 'Dispensing Desk' : 'Pharmacy Operations'}
                </p>
              </div>}
          </div>
          {!collapsed && <span className="mt-4 inline-flex rounded-md border border-teal-100 bg-teal-50 px-2.5 py-1 text-[11px] font-semibold text-teal-800" data-id="element-5100" data-mp="1r3pz9jwt0jcs">
              {manager ? 'Administrative rights' : dispenseOnly ? 'Dispensing & prescription forms' : 'Live inventory ledger'}
            </span>}
        </div>
        <div className="flex-1 overflow-y-auto px-3 py-5" data-id="element-5101" data-mp="34yyc7cb1mlo2">{navigation()}</div>
        <div className="border-t border-slate-100 p-3" data-id="element-5102" data-mp="351c81ndjs1wo">
          <button type="button" onClick={() => setCollapsed(value => !value)} className="flex w-full items-center justify-center rounded-md py-2 text-slate-400 hover:bg-slate-50 hover:text-slate-700" aria-label={collapsed ? 'Expand pharmacy navigation' : 'Collapse pharmacy navigation'} data-id="element-5103" data-mp="3spverygy86mn">
            {collapsed ? <ChevronRightIcon className="h-4 w-4" data-id="element-5104" data-mp="fg7hanyecua7" /> : <ChevronLeftIcon className="h-4 w-4" data-id="element-5105" data-mp="kak1x1bm0bnj" />}
          </button>
        </div>
      </aside>
      {mobileOpen && <div className="fixed inset-0 z-50 flex lg:hidden" data-id="element-5106" data-mp="u2mxtm60u0ke">
          <button type="button" aria-label="Close pharmacy navigation" className="absolute inset-0 bg-slate-950/35" onClick={() => setMobileOpen(false)} data-id="element-5107" data-mp="p77jyu80k4nz" />
          <aside className="relative flex h-full w-[280px] flex-col bg-white shadow-2xl" data-id="element-5108" data-mp="f7gs70au83rl">
            <div className="flex items-center justify-between border-b border-slate-100 p-5" data-id="element-5109" data-mp="b7ods6yrj3eb">
              <div data-id="element-5110" data-mp="6dtyrgu3rclk">
                <p className="text-sm font-bold text-slate-900" data-id="element-5111" data-mp="6dbsrshn1k0g">BMED Care</p>
                <p className="text-xs font-medium text-pink-600" data-id="element-5112" data-mp="1gtsb4tcc7tx">
                  {manager ? 'Pharmacist' : dispenseOnly ? 'Dispensing Desk' : 'Pharmacy Operations'}
                </p>
              </div>
              <button type="button" onClick={() => setMobileOpen(false)} className="rounded-md p-2 text-slate-500 hover:bg-slate-100" aria-label="Close pharmacy navigation" data-id="element-5113" data-mp="3jn85h1jscdr9">
                <XIcon className="h-5 w-5" data-id="element-5114" data-mp="284knbtn3xri3" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4" data-id="element-5115" data-mp="282rhggkbjbb5">{navigation(true)}</div>
          </aside>
        </div>}
      <section className="min-w-0 flex-1" data-id="element-5116" data-mp="1fg6q3g3uez7l">
        <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 px-4 py-4 backdrop-blur sm:px-6 lg:px-8" data-id="element-5117" data-mp="1fh7zzh94hfnq">
          <div className="mx-auto flex max-w-[1600px] items-center justify-between gap-4" data-id="element-5118" data-mp="1kcx5s2bqjssh">
            <div className="flex min-w-0 items-center gap-3" data-id="element-5119" data-mp="1pcrx26zfake2">
              <button type="button" onClick={() => setMobileOpen(true)} className="rounded-md border border-slate-200 p-2 text-slate-600 hover:bg-slate-50 lg:hidden" aria-label="Open pharmacy navigation" data-id="element-5120" data-mp="1u52yxfdq4g99">
                <MenuIcon className="h-5 w-5" data-id="element-5121" data-mp="33d66ooyrm2ai" />
              </button>
              <div data-id="element-5122" data-mp="33bbgow8mzjzk">
                <p className="text-xs font-medium text-slate-500" data-id="element-5123" data-mp="2zccehjeai1bt">
                  {manager ? 'Pharmacist' : 'Pharmacy'}{' '}
                  <span className="mx-1 text-slate-300" data-id="element-5124" data-mp="3d4iun5hg08b9">/</span>{' '}
                  {dispenseOnly ? 'Dispensing' : 'Operations'}
                </p>
                <h1 className="mt-0.5 text-lg font-bold text-slate-900 sm:text-xl" data-id="element-5125" data-mp="3n1na6mgs95j3">
                  {groups.flatMap(group => group.items).find(item => item.id === section)?.label}
                </h1>
              </div>
            </div>
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700" data-id="element-5126" data-mp="3r30h6zvm3erl">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" data-id="element-5127" data-mp="21nivpr0lav89" /> Live
              data
            </span>
          </div>
        </header>
        <main className="mx-auto max-w-[1600px] p-4 sm:p-6 lg:p-8" data-id="element-5128" data-mp="1wrbz4ou32w2z">
          {content()}
        </main>
      </section>
    </div>;
}